import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ArchiveCard from "@/components/feature/ArchiveCard";
import FaqList from "@/components/feature/FaqList";
import HomeHero from "@/pages/home/components/HomeHero";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { categories, events, faqs, voices, siteInfo } from "@/mocks/archive";
import { getEraShort } from "@/utils/archive";

export default function Home() {
  const featured = events.filter((e) => e.featured).slice(0, 3);
  const latest = [...events].sort((a, b) => b.year - a.year).slice(0, 6);
  const chrono = [...events].sort((a, b) => a.year - b.year);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: siteInfo.name,
        url: SITE_ORIGIN,
        description: siteInfo.description,
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_ORIGIN}/archive?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        url: SITE_ORIGIN,
        email: siteInfo.email,
        description: siteInfo.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
    ],
    []
  );

  useSeo({
    title: "暗线档案 | 暗黑事件时间线 · 暗黑时间轴与黑历史脉络梳理",
    description:
      "暗线档案按时间线梳理暗黑系事件，从暗黑时间轴到黑料编年，还原暗黑史与黑历史脉络。用清晰的时间顺序串联文明兴衰、未解之谜与重大事故，看懂事件演变全程。",
    keywords:
      "暗黑事件时间线,暗黑时间轴,黑料编年,暗黑史,黑历史梳理,暗黑脉络,未解之谜档案",
    path: "/",
    jsonLd,
  });

  return (
    <SiteLayout>
      <HomeHero />

      {/* 主编推荐 */}
      <section className="w-full px-4 md:px-8 py-16 md:py-20 border-b border-background-200">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-accent-400">Editor Picks</span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              本期重点整理
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-foreground-500">
              三条资料密度最高、时间线最完整的档案。每一条都附带分阶段的关键节点，方便按顺序完整阅读。
            </p>
          </div>
          <Link
            to="/archive"
            className="shrink-0 inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 cursor-pointer transition-colors"
          >
            查看全部档案
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((event) => (
            <ArchiveCard key={event.id} event={event} />
          ))}
        </div>
      </section>

      {/* 主题分类 */}
      <section className="w-full px-4 md:px-8 py-16 md:py-20 border-b border-background-200 bg-background-100">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-secondary-300">Index</span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            四个主题分类
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-foreground-500">
            按事件性质归档，方便你从自己关心的线索切入，再顺着时间轴向外扩展。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {categories.map((cat) => {
            const count = events.filter((e) => e.category === cat.id).length;
            return (
              <Link
                key={cat.id}
                to={`/archive?cat=${cat.id}`}
                className="group flex flex-col h-full p-6 rounded-lg border border-background-200 bg-background-50 hover:border-primary-700 cursor-pointer transition-colors"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-950 text-primary-400">
                  <i className={`${cat.icon} text-2xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-400 transition-colors">
                  {cat.name}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-500">{cat.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-xs text-secondary-300">
                  <i className="ri-archive-2-line" aria-hidden="true"></i>
                  已收录 {count} 条档案
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 时间轴预览 */}
      <section className="w-full px-4 md:px-8 py-16 md:py-20 border-b border-background-200">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-primary-400">Timeline</span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              时间轴预览
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-foreground-500">
              从公元前到当代，事件按照真实年份依次排列。点击任意节点即可进入该条档案的完整脉络。
            </p>
          </div>
          <Link
            to="/timeline"
            className="shrink-0 inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 cursor-pointer transition-colors"
          >
            查看完整时间轴
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        <ol className="relative border-l border-background-300 pl-6 md:pl-10 space-y-8">
          {chrono.slice(0, 5).map((event) => (
            <li key={event.id} className="relative">
              <span className="absolute -left-[31px] md:-left-[47px] top-1.5 w-2.5 h-2.5 rounded-full bg-accent-500 ring-4 ring-background-50"></span>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-heading text-lg font-bold text-primary-400">
                  {event.yearLabel}
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-secondary-900/40 text-secondary-200">
                  {getEraShort(event.era)}
                </span>
              </div>
              <h3 className="mt-2 font-heading text-base md:text-lg font-semibold text-foreground-900">
                <Link
                  to={`/archive/${event.id}`}
                  className="cursor-pointer hover:text-primary-400 transition-colors"
                >
                  {event.title}
                </Link>
              </h3>
              <p className="mt-1 text-sm text-foreground-500 line-clamp-2">{event.summary}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* 最新收录 */}
      <section className="w-full px-4 md:px-8 py-16 md:py-20 border-b border-background-200 bg-background-100">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-accent-400">Latest</span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              最近更新的档案
            </h2>
          </div>
          <Link
            to="/archive"
            className="shrink-0 inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 cursor-pointer transition-colors"
          >
            浏览档案库
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {latest.map((event) => (
            <ArchiveCard key={event.id} event={event} />
          ))}
        </div>
      </section>

      {/* 读者口碑 */}
      <section className="w-full px-4 md:px-8 py-16 md:py-20 border-b border-background-200">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-secondary-300">Readers</span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            读者怎么说
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {voices.map((voice) => (
            <blockquote
              key={voice.name}
              className="flex flex-col h-full p-6 rounded-lg border border-background-200 bg-background-100"
            >
              <i className="ri-double-quotes-l text-2xl text-primary-500/70" aria-hidden="true"></i>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground-600">{voice.text}</p>
              <footer className="mt-5 pt-4 border-t border-background-200">
                <span className="block font-heading text-sm font-semibold text-foreground-900">
                  {voice.name}
                </span>
                <span className="mt-0.5 block text-xs text-foreground-500">{voice.role}</span>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>

      {/* 常见问题 */}
      <section className="w-full px-4 md:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <span className="text-xs tracking-[0.3em] uppercase text-primary-400">FAQ</span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              常见问题
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500">
              关于栏目组织方式、年代标注与内容边界的说明。如果还有疑问，欢迎通过邮件与我们联系。
            </p>
            <a
              href={`mailto:${siteInfo.email}`}
              className="mt-6 inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 cursor-pointer transition-colors"
            >
              <i className="ri-mail-line" aria-hidden="true"></i>
              {siteInfo.email}
            </a>
          </div>
          <div className="lg:col-span-2">
            <FaqList items={faqs} />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}